/**
 * Device Manager
 * Handles generation, persistence, and retrieval of unique device identifiers.
 * Uses browser fingerprinting with secure UUID generation for deterministic device identification.
 */

class DeviceManager {
  constructor() {
    this.DEVICE_ID_KEY = 'app_device_id';
    this.DEVICE_FINGERPRINT_KEY = 'app_device_fingerprint';
  }

  /**
   * Generate a browser fingerprint based on device/browser characteristics
   * This ensures the same device generates the same fingerprint
   * @returns {string} - Deterministic fingerprint string
   */
  generateBrowserFingerprint() {
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
      deviceMemory: navigator.deviceMemory || 'unknown',
      maxTouchPoints: navigator.maxTouchPoints || 0,
    };

    // Create a deterministic hash of the fingerprint
    return this.hashObject(fingerprint);
  }

  /**
   * Generate a deterministic UUID v5-like identifier
   * Uses namespace and fingerprint to ensure consistency
   * @returns {string} - UUID-formatted device identifier
   */
  generateDeterministicUUID() {
    const fingerprint = this.generateBrowserFingerprint();
    const namespace = 'app-device-namespace';

    // Create a simple deterministic hash combining namespace and fingerprint
    const combined = namespace + fingerprint;
    const hash = this.simpleHash(combined);

    // Format as UUID v4-like string for readability
    const uuid = [
      hash.substr(0, 8),
      hash.substr(8, 4),
      '4' + hash.substr(12, 3), // version 4
      this.getRandomHex(4),
      hash.substr(16, 12),
    ].join('-');

    return uuid;
  }

  /**
   * Simple hash function for consistent hashing across sessions
   * @param {string} str - String to hash
   * @returns {string} - Hex hash
   */
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).padStart(32, '0');
  }

  /**
   * Create a hash of an object for fingerprinting
   * @param {object} obj - Object to hash
   * @returns {string} - Hex hash
   */
  hashObject(obj) {
    const str = JSON.stringify(obj);
    return this.simpleHash(str);
  }

  /**
   * Get random hex string of specified length
   * @param {number} length - Length of hex string
   * @returns {string} - Random hex string
   */
  getRandomHex(length) {
    return Math.floor(Math.random() * Math.pow(16, length))
      .toString(16)
      .padStart(length, '0');
  }

  /**
   * Get or create device identifier
   * On first call, generates and persists a new device ID
   * On subsequent calls, returns the persisted device ID
   * @returns {string} - Device identifier
   */
  getDeviceId() {
    try {
      let deviceId = sessionStorage.getItem(this.DEVICE_ID_KEY);

      // Check localStorage as fallback for persistence across browser sessions
      if (!deviceId) {
        deviceId = localStorage.getItem(this.DEVICE_ID_KEY);
      }

      // If no device ID exists, generate and persist one
      if (!deviceId) {
        deviceId = this.generateDeterministicUUID();
        this.persistDeviceId(deviceId);
      }

      return deviceId;
    } catch (error) {
      console.error('Error getting device ID:', error);
      return this.generateDeterministicUUID();
    }
  }

  /**
   * Persist device identifier to storage
   * Uses both sessionStorage (temporary) and localStorage (persistent) for redundancy
   * @param {string} deviceId - Device identifier to persist
   */
  persistDeviceId(deviceId) {
    try {
      sessionStorage.setItem(this.DEVICE_ID_KEY, deviceId);
      localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
    } catch (error) {
      console.error('Error persisting device ID:', error);
    }
  }

  /**
   * Get current browser fingerprint
   * Used to validate device hasn't changed
   * @returns {string} - Current fingerprint
   */
  getCurrentFingerprint() {
    return this.generateBrowserFingerprint();
  }

  /**
   * Check if device fingerprint has changed
   * Indicates device/browser characteristics have changed
   * @returns {boolean} - true if fingerprint changed, false if same
   */
  hasDeviceFingerprintChanged() {
    try {
      const storedFingerprint = localStorage.getItem(this.DEVICE_FINGERPRINT_KEY);
      const currentFingerprint = this.getCurrentFingerprint();

      if (!storedFingerprint) {
        // First time - store the fingerprint
        this.storeFingerprint(currentFingerprint);
        return false;
      }

      return storedFingerprint !== currentFingerprint;
    } catch (error) {
      console.error('Error checking device fingerprint:', error);
      return false;
    }
  }

  /**
   * Store current fingerprint for future comparison
   * @param {string} fingerprint - Fingerprint to store
   */
  storeFingerprint(fingerprint) {
    try {
      localStorage.setItem(this.DEVICE_FINGERPRINT_KEY, fingerprint);
    } catch (error) {
      console.error('Error storing fingerprint:', error);
    }
  }

  /**
   * Clear device identification (use on logout or device reset)
   * Only clears if explicitly requested - respects backend device policy
   */
  clearDeviceId() {
    try {
      sessionStorage.removeItem(this.DEVICE_ID_KEY);
      localStorage.removeItem(this.DEVICE_ID_KEY);
      localStorage.removeItem(this.DEVICE_FINGERPRINT_KEY);
    } catch (error) {
      console.error('Error clearing device ID:', error);
    }
  }

  /**
   * Reset device identifier (generates new one)
   * Use only when explicitly required by backend policy
   */
  resetDeviceId() {
    try {
      this.clearDeviceId();
      const newDeviceId = this.generateDeterministicUUID();
      this.persistDeviceId(newDeviceId);
      return newDeviceId;
    } catch (error) {
      console.error('Error resetting device ID:', error);
      return null;
    }
  }
}

export const deviceManager = new DeviceManager();
